import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import api from '../services/api'

interface User {
  id: string
  email: string
  name: string
  role: string
  isActive: boolean
  createdAt: string
}

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const { logout } = useAuth()

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users')
      setUsers(response.data.users)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleActive = async (id: string, isActive: boolean) => {
    const msg = isActive ? 'このアカウントを停止しますか？' : 'このアカウントを有効化しますか？'
    if (!window.confirm(msg)) return
    try {
      await api.put(`/users/${id}`, { isActive: !isActive })
      fetchUsers()
    } catch {
      alert('更新に失敗しました')
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`「${name}」を削除しますか？\n※予約データも削除されます。この操作は取り消せません。`)) return
    try {
      await api.delete(`/users/${id}`)
      fetchUsers()
    } catch {
      alert('削除に失敗しました')
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })

  const filtered = users.filter(u =>
    u.name.includes(search) || u.email.includes(search)
  )

  if (loading) return <div style={styles.loading}>読み込み中...</div>

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>会員一覧</h1>
        <div style={styles.headerButtons}>
          <a href="/admin" style={styles.backButton}>← ダッシュボード</a>
          <button onClick={logout} style={styles.logoutButton}>ログアウト</button>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>会員一覧（{filtered.length}件）</h2>
          <input
            type="text"
            placeholder="名前・メールで検索"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {filtered.length === 0 ? (
          <p style={styles.empty}>会員が見つかりません</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>氏名</th>
                <th style={styles.th}>メールアドレス</th>
                <th style={styles.th}>権限</th>
                <th style={styles.th}>登録日</th>
                <th style={styles.th}>ステータス</th>
                <th style={styles.th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user, idx) => (
                <tr key={user.id} style={{ backgroundColor: idx % 2 === 1 ? '#f9f9f9' : 'white' }}>
                  <td style={styles.td}>{user.name}</td>
                  <td style={styles.td}>{user.email}</td>
                  <td style={styles.td}>
                    <span style={{
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      backgroundColor: user.role === 'ADMIN' ? '#1F4E79' : '#eee',
                      color: user.role === 'ADMIN' ? 'white' : '#555'
                    }}>
                      {user.role === 'ADMIN' ? '管理者' : '会員'}
                    </span>
                  </td>
                  <td style={styles.td}>{formatDate(user.createdAt)}</td>
                  <td style={styles.td}>
                    <span style={{
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      backgroundColor: user.isActive ? '#d8f0e3' : '#fde8e8',
                      color: user.isActive ? '#1D6B3A' : '#e74c3c'
                    }}>
                      {user.isActive ? '有効' : '停止中'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {user.role !== 'ADMIN' && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleToggleActive(user.id, user.isActive)}
                          style={{
                            padding: '4px 12px',
                            backgroundColor: user.isActive ? '#e67e22' : '#27ae60',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {user.isActive ? '停止' : '有効化'}
                        </button>
                        <button
                          onClick={() => handleDelete(user.id, user.name)}
                          style={{
                            padding: '4px 12px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          削除
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '20px', maxWidth: '1200px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  headerButtons: { display: 'flex', gap: '12px', alignItems: 'center' },
  title: { color: '#1F4E79', margin: 0 },
  backButton: { padding: '8px 16px', backgroundColor: '#3498db', color: 'white', borderRadius: '4px', textDecoration: 'none', fontSize: '14px' },
  logoutButton: { padding: '8px 16px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  section: { backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  sectionTitle: { color: '#333', margin: 0 },
  searchInput: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', width: '240px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { backgroundColor: '#1F4E79', color: 'white', padding: '10px', textAlign: 'left', fontSize: '14px' },
  td: { padding: '10px', borderBottom: '1px solid #eee', fontSize: '14px' },
  loading: { textAlign: 'center', padding: '40px' },
  empty: { color: '#888', textAlign: 'center', padding: '20px' }
}
